import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import SettingsLayout from '@/layouts/settings/layout';
import { laravelJsonFetch, LaravelJsonRequestError } from '@/lib/laravelJsonFetch';
import { Head } from '@inertiajs/react';
import { AlertTriangle, CheckCircle2, Loader2, PlayCircle, RefreshCw, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import Heading from '@/components/layout/Heading';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/shadcn/alert';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Separator } from '@/components/ui/shadcn/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import FolderTemplateCard from '@/pages/settings/sharepoint/FolderTemplateCard';
import LegacyBackfillCard from '@/pages/settings/sharepoint/LegacyBackfillCard';

interface TokenMeta {
    expires_at: string;
    expires_in: number;
    fetched_at: string;
}

interface SyncRow {
    uuid: string;
    contract_number: string | null;
    contract_name: string | null;
    sharepoint_provision_status: string | null;
    sharepoint_last_synced_at: string | null;
    sharepoint_web_url: string | null;
}

interface ErrorRow {
    id: number;
    level: string;
    action: string;
    item_id: string | null;
    message: string;
    created_at: string;
}

interface PageProps {
    config: {
        tenant_id_set: boolean;
        client_id_set: boolean;
        client_secret_set: boolean;
        site_id: string | null;
        drive_id: string | null;
        root_path: string;
        cache_ttl: number;
        alert_email: string;
        max_upload_bytes: number;
    };
    tokenMeta: TokenMeta | null;
    folderTemplate: string[];
    folderTemplateDefault: string[];
    stats: {
        total_files: number;
        projects_provisioned: number;
        projects_failed: number;
    };
    recentSyncs: SyncRow[];
    recentErrors: ErrorRow[];
}

interface StepResult {
    ok: boolean;
    durationMs: number;
    response?: unknown;
    error?: string;
    statusCode?: number;
}

type StepId = 'auth' | 'site' | 'drive' | 'rootFolder' | 'createFolder' | 'uploadFile' | 'deleteFolder';

const STEPS: { id: StepId; label: string; helper: string }[] = [
    { id: 'auth', label: '1. Acquire access token', helper: 'Validates tenant id, client id, and client secret.' },
    { id: 'site', label: '2. Read SharePoint site', helper: 'Validates site id and that the app has site-level access.' },
    { id: 'drive', label: '3. Read drive (document library)', helper: 'Validates drive id within the site.' },
    { id: 'rootFolder', label: '4. List root folder contents', helper: 'Verifies the configured root path exists.' },
    { id: 'createFolder', label: '5. Create a disposable test folder', helper: 'Confirms write access to the root.' },
    { id: 'uploadFile', label: '6. Upload a small test file', helper: 'Requires step 5 to have produced a folder id.' },
    { id: 'deleteFolder', label: '7. Delete the test folder', helper: 'Cleans up everything created above.' },
];

function endpointFor(step: StepId): string {
    switch (step) {
        case 'auth':
            return '/settings/integrations/sharepoint/test/auth';
        case 'site':
            return '/settings/integrations/sharepoint/test/site';
        case 'drive':
            return '/settings/integrations/sharepoint/test/drive';
        case 'rootFolder':
            return '/settings/integrations/sharepoint/test/root-folder';
        case 'createFolder':
            return '/settings/integrations/sharepoint/test/create-folder';
        case 'uploadFile':
            return '/settings/integrations/sharepoint/test/upload-file';
        case 'deleteFolder':
            return '/settings/integrations/sharepoint/test/delete-folder';
    }
}

function formatBytes(bytes: number): string {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let n = bytes;
    let u = 0;
    while (n >= 1024 && u < units.length - 1) {
        n /= 1024;
        u += 1;
    }
    return `${n.toFixed(n >= 100 ? 0 : 1)} ${units[u]}`;
}

function StatusBadge({ status }: { status: string | null }) {
    if (!status) return <Badge variant="outline">unknown</Badge>;
    if (status === 'ready') return <Badge variant="default">ready</Badge>;
    if (status === 'failed') return <Badge variant="destructive">failed</Badge>;
    return <Badge variant="secondary">{status}</Badge>;
}

export default function SharePointSettings({
    config,
    tokenMeta,
    folderTemplate,
    folderTemplateDefault,
    stats,
    recentSyncs,
    recentErrors,
}: PageProps) {
    const { t } = useTranslations();
    const [results, setResults] = useState<Partial<Record<StepId, StepResult>>>({});
    const [busy, setBusy] = useState<Partial<Record<StepId, boolean>>>({});
    const [lastFolderId, setLastFolderId] = useState<string | null>(null);

    const allCredsConfigured = config.tenant_id_set && config.client_id_set && config.client_secret_set && !!config.site_id && !!config.drive_id;

    const runStep = async (step: StepId) => {
        setBusy((b) => ({ ...b, [step]: true }));
        const body: Record<string, unknown> = {};
        if (step === 'uploadFile' || step === 'deleteFolder') {
            if (!lastFolderId) {
                toast.error(t('toasts.sharepoint_index.create_folder_first', 'Run step 5 (Create test folder) first.'));
                setBusy((b) => ({ ...b, [step]: false }));
                return;
            }
            body.folder_id = lastFolderId;
        }

        try {
            const result = await laravelJsonFetch<StepResult>(endpointFor(step), 'POST', body);
            setResults((r) => ({ ...r, [step]: result }));
            if (step === 'createFolder' && result.ok) {
                const id = (result.response as { id?: string } | undefined)?.id;
                if (id) {
                    setLastFolderId(id);
                }
            }
            if (step === 'deleteFolder' && result.ok) {
                setLastFolderId(null);
            }
            if (result.ok) {
                toast.success(
                    t('toasts.sharepoint_index.step_succeeded', ':step succeeded in :ms ms')
                        .replace(':step', String(step))
                        .replace(':ms', String(result.durationMs)),
                );
            } else {
                toast.error(
                    t('toasts.sharepoint_index.step_failed', ':step failed: :error')
                        .replace(':step', String(step))
                        .replace(':error', String(result.error ?? 'unknown error')),
                );
            }
        } catch (err) {
            const message = err instanceof LaravelJsonRequestError ? err.message : (err as Error).message;
            toast.error(t('toasts.sharepoint_index.request_failed', 'Request failed: :message').replace(':message', String(message)));
            setResults((r) => ({
                ...r,
                [step]: { ok: false, durationMs: 0, error: message },
            }));
        } finally {
            setBusy((b) => ({ ...b, [step]: false }));
        }
    };

    const runAll = async () => {
        for (const step of STEPS.map((s) => s.id)) {
            await runStep(step);
        }
    };

    return (
        <AppLayout>
            <Head title="SharePoint Integration" />
            <SettingsLayout>
                <div className="space-y-6">
                    <Heading title="SharePoint Integration" description="Configure and test the M365 file sync." />

                    {!allCredsConfigured && (
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertTitle>Configuration incomplete</AlertTitle>
                            <AlertDescription>
                                Set the SP_TENANT_ID, SP_CLIENT_ID, SP_CLIENT_SECRET, SP_SITE_ID, and SP_DRIVE_ID values in <code>.env</code>, then
                                restart Octane. Until all values are present, the test harness will fail at step 1.
                            </AlertDescription>
                        </Alert>
                    )}

                    <Card>
                        <CardHeader>
                            <CardTitle>Current configuration</CardTitle>
                            <CardDescription>
                                Read-only — change values in <code>.env</code> and restart Octane.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="grid gap-3 text-sm md:grid-cols-2">
                            <div>
                                <div className="text-muted-foreground">Tenant ID</div>
                                <div>
                                    {config.tenant_id_set ? (
                                        <Badge variant="default">configured</Badge>
                                    ) : (
                                        <Badge variant="destructive">missing</Badge>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Client ID</div>
                                <div>
                                    {config.client_id_set ? (
                                        <Badge variant="default">configured</Badge>
                                    ) : (
                                        <Badge variant="destructive">missing</Badge>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Client Secret</div>
                                <div>
                                    {config.client_secret_set ? (
                                        <Badge variant="default">configured</Badge>
                                    ) : (
                                        <Badge variant="destructive">missing</Badge>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Site ID</div>
                                <div className="font-mono text-xs break-all">{config.site_id ?? '—'}</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Drive ID</div>
                                <div className="font-mono text-xs break-all">{config.drive_id ?? '—'}</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Root path</div>
                                <div className="font-mono text-xs">{config.root_path}</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Cache TTL</div>
                                <div>{config.cache_ttl}s</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Alert email</div>
                                <div>{config.alert_email}</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Max upload</div>
                                <div>{formatBytes(config.max_upload_bytes)}</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Token cached</div>
                                <div>
                                    {tokenMeta ? (
                                        <span className="font-mono text-xs">expires {new Date(tokenMeta.expires_at).toLocaleString()}</span>
                                    ) : (
                                        <Badge variant="outline">none</Badge>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-start justify-between gap-3">
                            <div>
                                <CardTitle>Test connection</CardTitle>
                                <CardDescription>
                                    Run each step individually to pinpoint failures. Steps 5–7 share state through the created folder id.
                                </CardDescription>
                            </div>
                            <Button type="button" onClick={runAll} disabled={Object.values(busy).some(Boolean)}>
                                <PlayCircle className="mr-2 h-4 w-4" /> Run all
                            </Button>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {STEPS.map((step) => {
                                const result = results[step.id];
                                const isBusy = busy[step.id];
                                return (
                                    <div key={step.id} className="rounded-md border p-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <div className="text-sm font-medium">{step.label}</div>
                                                <div className="text-muted-foreground text-xs">{step.helper}</div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {result && (
                                                    <>
                                                        {result.ok ? (
                                                            <Badge variant="default" className="gap-1">
                                                                <CheckCircle2 className="h-3 w-3" /> ok ({result.durationMs}ms)
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="destructive" className="gap-1">
                                                                <XCircle className="h-3 w-3" /> failed ({result.durationMs}ms)
                                                            </Badge>
                                                        )}
                                                    </>
                                                )}
                                                <Button type="button" variant="outline" size="sm" onClick={() => runStep(step.id)} disabled={isBusy}>
                                                    {isBusy ? (
                                                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <RefreshCw className="mr-2 h-3 w-3" />
                                                    )}
                                                    Run
                                                </Button>
                                            </div>
                                        </div>
                                        {result && (
                                            <details className="mt-3">
                                                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs">
                                                    {result.ok ? 'Show response' : 'Show error details'}
                                                </summary>
                                                <pre className="bg-muted mt-2 max-h-72 overflow-auto rounded p-3 text-xs">
                                                    {JSON.stringify(
                                                        result.ok
                                                            ? result.response
                                                            : {
                                                                  error: result.error,
                                                                  statusCode: result.statusCode,
                                                                  response: (result as { response?: unknown }).response,
                                                              },
                                                        null,
                                                        2,
                                                    )}
                                                </pre>
                                            </details>
                                        )}
                                    </div>
                                );
                            })}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Health</CardTitle>
                            <CardDescription>Live counts from the database.</CardDescription>
                        </CardHeader>
                        <CardContent className="grid grid-cols-3 gap-3 text-sm">
                            <div>
                                <div className="text-muted-foreground">Total files indexed</div>
                                <div className="text-2xl font-semibold">{stats.total_files}</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Projects with SharePoint folder</div>
                                <div className="text-2xl font-semibold">{stats.projects_provisioned}</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Projects failed to provision</div>
                                <div className="text-destructive text-2xl font-semibold">{stats.projects_failed}</div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Recently synced projects</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {recentSyncs.length === 0 ? (
                                <div className="text-muted-foreground text-sm">No projects have been synced yet.</div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Project</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Last synced</TableHead>
                                            <TableHead className="text-right">Folder</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {recentSyncs.map((row) => (
                                            <TableRow key={row.uuid}>
                                                <TableCell className="font-mono text-xs">
                                                    {row.contract_number ?? row.uuid}{' '}
                                                    <span className="text-muted-foreground">{row.contract_name ?? ''}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <StatusBadge status={row.sharepoint_provision_status} />
                                                </TableCell>
                                                <TableCell className="text-xs">
                                                    {row.sharepoint_last_synced_at ? new Date(row.sharepoint_last_synced_at).toLocaleString() : '—'}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {row.sharepoint_web_url ? (
                                                        <a
                                                            className="text-primary text-xs underline"
                                                            href={row.sharepoint_web_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Open
                                                        </a>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>

                    {recentErrors.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle>Recent errors</CardTitle>
                                <CardDescription>
                                    Most recent 20 error-level entries from <code>sharepoint_sync_log</code>.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>When</TableHead>
                                            <TableHead>Action</TableHead>
                                            <TableHead>Message</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {recentErrors.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell className="text-xs">{new Date(row.created_at).toLocaleString()}</TableCell>
                                                <TableCell className="font-mono text-xs">{row.action}</TableCell>
                                                <TableCell className="text-xs">{row.message}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}

                    <Separator />

                    <FolderTemplateCard initial={folderTemplate} defaults={folderTemplateDefault} />

                    <Separator />

                    <LegacyBackfillCard />

                    <Separator />

                    <div className="text-muted-foreground text-xs">
                        Setup guide: see <code>SHAREPOINT_SETUP.md</code> at the repo root for the Azure AD app registration and PowerShell{' '}
                        <code>Sites.Selected</code> grant steps.
                    </div>
                </div>
            </SettingsLayout>
        </AppLayout>
    );
}
